/**
 * Alert Rule Stack Card Component
 * 
 * A horizontal stacked card for displaying alert rules.
 * Features:
 * - Left border color indicator (green/yellow/red based on status)
 * - Column layout: Label & Type | Stats | Created At
 * - Expandable details section with summary and context
 * - Edit icon with "Coming soon!" tooltip
 */

import { Box, Collapse, Group, Stack, Text } from '@mantine/core';
import { CaretDown, CaretUp, CheckCircle, Clock, EnvelopeSimple, PencilSimple, Trash } from '@phosphor-icons/react';
import { memo, useState, type FC } from 'react';

import HoverTooltip from '@components/common/HoverTooltip';
import ScheduleReportModal from '@components/containers/reportSchedule/ScheduleReportModal';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { type AlertWithCounts, deleteUserAlert, getAlertTypeLabel } from '../api';

interface AlertRuleStackCardProps {
    alert: AlertWithCounts;
    onDelete?: (alertId: string) => void;
}

/**
 * Determines the left border color based on alert status
 * - Green: No pending items
 * - Yellow: Has pending items but none overdue
 * - Red: Has overdue pending items
 */
const getBorderColor = (alert: AlertWithCounts): string => {
    if (alert.pendingChecklistItems === 0) {
        return 'rgb(34, 197, 94)'; // Green - no pending
    }
    if (alert.hasOverduePending) {
        return 'rgb(239, 68, 68)'; // Red - overdue
    }
    return 'rgb(234, 179, 8)'; // Yellow - pending but not overdue
};

/**
 * Formats a date string to a readable format
 */
const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

/**
 * Formats context object into bullet point items
 */
const formatContextItems = (context: Record<string, unknown> | null): string[] => {
    if (!context) return [];
    
    const items: string[] = [];
    for (const [key, value] of Object.entries(context)) {
        if (value !== null && value !== undefined) {
            const formattedKey = key
                .replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .trim();
            
            let formattedValue = String(value);
            if (Array.isArray(value)) {
                formattedValue = value.join(', ');
            } else if (typeof value === 'object') {
                formattedValue = JSON.stringify(value);
            }
            
            items.push(`${formattedKey}: ${formattedValue}`);
        }
    }
    return items;
};

const AlertRuleStackCard: FC<AlertRuleStackCardProps> = memo(({ alert, onDelete }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const { currentUser } = useCurrentUser();

    const typeLabel = getAlertTypeLabel(alert.alertType);
    const displayLabel = alert.alertLabel || typeLabel;
    const borderColor = getBorderColor(alert);
    const hasPending = alert.pendingChecklistItems > 0;
    const pendingColor = alert.hasOverduePending ? 'rgb(239, 68, 68)' : 'rgb(234, 179, 8)';

    const contextItems = formatContextItems(alert.context);
    const hasExpandableContent = alert.summaryDescription || contextItems.length > 0;

    const handleDelete = async () => {
        if (isDeleting) return;
        const confirmed = window.confirm(
            `Delete alert "${displayLabel}"? This cannot be undone.`
        );
        if (!confirmed) return;

        setIsDeleting(true);
        const result = await deleteUserAlert(alert.alertId);
        setIsDeleting(false);
        if (result.ok) {
            onDelete?.(alert.alertId);
        } else {
            window.alert(`Failed to delete alert.\n\n${result.message}`);
        }
    };

    return (
        <Box
            style={{
                backgroundColor: '#FFFFFF',
                borderRadius: 12,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                overflow: 'hidden',
            }}
        >
            {/* Main content row with left border indicator */}
            <Box 
                style={{ 
                    padding: '16px 20px 16px 24px',
                    position: 'relative',
                }}
            >
                {/* Left border indicator - only on main content */}
                <Box 
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        backgroundColor: borderColor,
                        borderRadius: isExpanded ? '12px 0 0 0' : '12px 0 0 12px',
                    }}
                />
                <Group justify="space-between" wrap="nowrap">
                    {/* Column 1: Label and Type */}
                    <Stack gap={2} style={{ flex: 2, minWidth: 0 }}>
                        <Text 
                            style={{ 
                                fontSize: 10,
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                color: 'rgb(148, 163, 184)',
                            }}
                        >
                            {typeLabel}
                        </Text>
                        <Text 
                            style={{ 
                                fontSize: 15,
                                fontWeight: 600,
                                lineHeight: 1.3,
                                color: 'rgb(26, 32, 44)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {displayLabel}
                        </Text>
                    </Stack>

                    {/* Column 2: Stats */}
                    <Group gap={16} style={{ flex: 1.5 }} justify="center">
                        <Group gap={4}>
                            <CheckCircle size={14} style={{ color: 'rgb(148, 163, 184)' }} />
                            <Text style={{ fontSize: 12, color: 'rgb(100, 116, 139)' }}>
                                {alert.totalChecklistItems} items
                            </Text>
                        </Group>
                        {hasPending && (
                            <Group gap={4}>
                                <Clock size={14} style={{ color: pendingColor }} />
                                <Text style={{ fontSize: 12, color: pendingColor, fontWeight: 500 }}>
                                    {alert.pendingChecklistItems} pending
                                </Text>
                            </Group>
                        )}
                    </Group>

                    {/* Column 3: Created At */}
                    <Box style={{ flex: 1, textAlign: 'center' }}>
                        <Text style={{ fontSize: 12, color: 'rgb(148, 163, 184)' }}>
                            Created on {formatDate(alert.createdAt)}
                        </Text>
                    </Box>

                    {/* Schedule email icon */}
                    <HoverTooltip label="Email me this alert" position="left">
                        <Box
                            onClick={() => setScheduleOpen(true)}
                            style={{
                                cursor: 'pointer',
                                padding: 8,
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background-color 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <EnvelopeSimple size={18} style={{ color: 'rgb(100, 116, 139)' }} />
                        </Box>
                    </HoverTooltip>

                    {/* Expand/Collapse icon */}
                    {hasExpandableContent && (
                        <Box
                            onClick={() => setIsExpanded(!isExpanded)}
                            style={{
                                cursor: 'pointer',
                                padding: 8,
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background-color 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {isExpanded ? (
                                <CaretUp size={18} style={{ color: 'rgb(100, 116, 139)' }} />
                            ) : (
                                <CaretDown size={18} style={{ color: 'rgb(100, 116, 139)' }} />
                            )}
                        </Box>
                    )}
                    
                    {/* Placeholder for alignment when no expandable content */}
                    {!hasExpandableContent && <Box style={{ width: 34 }} />}
                </Group>
            </Box>

            {/* Expandable details section */}
            <Collapse in={isExpanded}>
                <Box
                    style={{
                        backgroundColor: 'rgb(248, 250, 252)',
                        padding: '16px 20px 16px 24px',
                        borderTop: '1px solid rgb(241, 245, 249)',
                    }}
                >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Stack gap={12} style={{ flex: 1 }}>
                            {/* Summary */}
                            {alert.summaryDescription && (
                                <Box>
                                    <Text 
                                        style={{ 
                                            fontSize: 13,
                                            color: 'rgb(51, 65, 85)',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        {alert.summaryDescription}
                                    </Text>
                                </Box>
                            )}

                            {/* Context as bullet points */}
                            {contextItems.length > 0 && (
                                <Stack gap={6}>
                                    <Text 
                                        style={{ 
                                            fontSize: 10,
                                            fontWeight: 600,
                                            letterSpacing: '0.05em',
                                            textTransform: 'uppercase',
                                            color: 'rgb(148, 163, 184)',
                                        }}
                                    >
                                        Parameters
                                    </Text>
                                    <Stack gap={4}>
                                        {contextItems.map((item, index) => (
                                            <Group key={index} gap={8} wrap="nowrap" align="flex-start">
                                                <Box
                                                    style={{
                                                        width: 4,
                                                        height: 4,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'rgb(148, 163, 184)',
                                                        marginTop: 6,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Text 
                                                    style={{ 
                                                        fontSize: 12,
                                                        color: 'rgb(100, 116, 139)',
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {item}
                                                </Text>
                                            </Group>
                                        ))}
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>

                        {/* Action icons: edit (stub) + delete */}
                        <Stack gap={4} align="center">
                            <HoverTooltip label="Coming soon!" position="left">
                                <Box
                                    style={{
                                        cursor: 'not-allowed',
                                        padding: 8,
                                        borderRadius: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: 0.5,
                                    }}
                                >
                                    <PencilSimple size={18} style={{ color: 'rgb(100, 116, 139)' }} />
                                </Box>
                            </HoverTooltip>
                            <HoverTooltip label="Delete alert" position="left">
                                <Box
                                    onClick={handleDelete}
                                    style={{
                                        cursor: isDeleting ? 'wait' : 'pointer',
                                        padding: 8,
                                        borderRadius: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: isDeleting ? 0.5 : 1,
                                        transition: 'background-color 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isDeleting) {
                                            e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <Trash size={18} style={{ color: 'rgb(239, 68, 68)' }} />
                                </Box>
                            </HoverTooltip>
                        </Stack>
                    </Group>
                </Box>
            </Collapse>

            {scheduleOpen && (
                <ScheduleReportModal
                    opened={scheduleOpen}
                    onClose={() => setScheduleOpen(false)}
                    defaultName={displayLabel}
                    alertId={alert.alertId}
                    currentUserEmail={currentUser?.email ?? ''}
                />
            )}
        </Box>
    );
});

AlertRuleStackCard.displayName = 'AlertRuleStackCard';

export default AlertRuleStackCard;
